'use client';

import type { SelectValuesFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import type { DialogProps } from '@packages/shared/src/ui/Dialog/Dialog';
import { getIsAvailable } from '../../../helpers/getIsAvailable/getIsAvailable';
import { VariationIcon } from '../../VariationIcon/VariationIcon';
import type { VariationSelectionOnSelectHandler } from '../../types';
import { useVariationFormattedLink } from '../../utils/useVariationFormattedLink';

export type VariationRowProps = VariationSelectionOnSelectHandler & {
  /**
   * The GraphQL value data for the given product
   */
  data: SelectValuesFragmentFragment['values'][number];
  /**
   * indicates if one value has an image and the column has to be rendered
   */
  renderImage: boolean;
  /* 
    the handler to close the dialog
  */
  onClose: NonNullable<DialogProps['onClose']>;
};

/**
 * VariationRow component is used to display a single row of the variation selection dialog.
 * */
export const VariationRow = ({ data, renderImage, onSelect, onClose }: VariationRowProps) => {
  const { sku, availability, isSelected, displayText, rgb, image, variationMainImage } = data;

  const isAvailable = getIsAvailable(availability);
  // button is not clickable if selected === true
  const isClickable = !(isSelected === true);
  const { getOnSelectCallback } = useVariationFormattedLink();
  const onClickHandler = getOnSelectCallback({ sku, onSelect });

  return (
    <>
      <button
        type="button"
        className={`hover:bg-color-grey-light flex w-full appearance-none items-center justify-between gap-1.5 p-1.5 ${isSelected ? 'bg-color-grey-light' : ''} ${isClickable ? 'cursor-pointer' : 'cursor-auto'}`}
        onClick={() => {
          if (isClickable) {
            onClickHandler();
            onClose();
          }
        }}
        disabled={!isClickable}
      >
        {renderImage && <VariationIcon mode="DIALOG" data={{ rgb, image, variationMainImage }} />}
        <span className="text-size-body-sm flex-grow text-start">{displayText}</span>
        {(() => {
          if (isAvailable) {
            /* is available and selected */
            if (isSelected) {
              return <span role="img" className="icon-emp-checkmark size-4 shrink-0" />;
            }
            /* is available and not selected */
            return <span role="img" className="icon-emp-arrow-medium-right size-4 shrink-0" />;
          }
          /* is not available */
          return (
            <span className="text-size-body-sm text-color-error font-semibold">
              {availability!.message}
            </span>
          );
        })()}
      </button>
      <Divider />
    </>
  );
};
